import { z } from 'zod';

export const failureTypes = [
  'doa',
  'early_failure',
  'customer_return',
  'tech_damage',
  'shipping_damage',
  'failed_testing',
  'other',
] as const;

export const failureDispositions = ['quarantine', 'rma', 'write_off', 'returned_to_stock'] as const;

export const managerDispositions = ['quarantine', 'rma', 'write_off'] as const;

export const rmaItemStatuses = ['pending', 'credited', 'denied', 'replaced'] as const;

export const createWarrantyTicketSchema = z.object({
  warrantyReason: z.string().trim().min(1).max(2000),
  repairIds: z.array(z.string().uuid()).min(1),
});

export const recordInstalledPartFailureSchema = z.object({
  installedTicketPartId: z.string().uuid(),
  failureType: z.enum(failureTypes),
  failureReason: z.string().max(2000).optional(),
  quarantineBinId: z.string().uuid().optional(),
  disposition: z.enum(failureDispositions).default('quarantine'),
  attributedTo: z.string().uuid().optional(),
});

export const recordDamageReportSchema = z.object({
  inventoryItemId: z.string().uuid(),
  inventorySerialId: z.string().uuid().optional(),
  quantity: z.number().int().positive().max(100).default(1),
  failureType: z.enum(failureTypes),
  failureReason: z.string().max(2000).optional(),
  supplierId: z.string().uuid().optional(),
  unitCost: z.number().nonnegative().optional(),
  quarantineBinId: z.string().uuid().optional(),
  disposition: z.enum(failureDispositions).default('quarantine'),
  attributedTo: z.string().uuid().optional(),
  warrantyTicketId: z.string().uuid().optional(),
  partName: z.string().max(255).optional(),
});

export const reclassifyFailureSchema = z.object({
  disposition: z.enum(managerDispositions),
  note: z.string().trim().min(1).max(2000),
});

export const quarantineBinSchema = z.object({
  name: z.string().trim().min(1).max(100),
  supplierId: z.string().uuid().nullable().optional(),
  location: z.string().max(100).nullable().optional(),
  active: z.boolean().optional(),
});

export const damageReportFilterSchema = z.object({
  reviewed: z.enum(['unreviewed', 'reviewed', 'all']).default('unreviewed'),
  disposition: z.enum(failureDispositions).optional(),
  attributedTo: z.string().uuid().optional(),
  failureType: z.enum(failureTypes).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});

export const resolveRmaSchema = z
  .object({
    mode: z.enum(['header', 'per_item']),
    creditAmount: z.number().nonnegative().optional(),
    creditMemoNumber: z.string().max(100).optional(),
    items: z
      .array(
        z.object({
          id: z.string().uuid(),
          itemStatus: z.enum(['credited', 'denied', 'replaced']),
          creditAmount: z.number().nonnegative().optional(),
          denialReason: z.string().max(2000).optional(),
          resolutionNote: z.string().max(2000).optional(),
        }),
      )
      .optional(),
  })
  .superRefine((val, ctx) => {
    if (val.mode === 'header' && val.creditAmount == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Header credit amount is required',
        path: ['creditAmount'],
      });
    }
    if (val.mode === 'per_item') {
      if (!val.items || val.items.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Per-item resolutions are required',
          path: ['items'],
        });
      }
      for (const [index, item] of (val.items ?? []).entries()) {
        if (item.itemStatus === 'denied' && !item.resolutionNote?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A note is required when an item is denied',
            path: ['items', index, 'resolutionNote'],
          });
        }
      }
    }
  });

export type CreateWarrantyTicketInput = z.infer<typeof createWarrantyTicketSchema>;
export type RecordInstalledPartFailureInput = z.infer<typeof recordInstalledPartFailureSchema>;
export type RecordDamageReportInput = z.infer<typeof recordDamageReportSchema>;
export type ReclassifyFailureInput = z.infer<typeof reclassifyFailureSchema>;
export type ResolveRmaInput = z.infer<typeof resolveRmaSchema>;
